import { Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ObjectSchema } from 'yup';
import { uiStateSelectors } from '../../../services/redux/features/uiState.slice';
import { MixedComponentsProps } from '../../../types/mixedComponentProps';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { createPersonalDetailsFormValidator } from '../validationSchema';
import { createFormInputs } from './formInputs';
import { PersonalDetailsFormNewModel } from './formModel';
import { personalDetailsInitialValues } from './initialValues';

export const PersonalDetailsFormNew_MX: React.FC<MixedComponentsProps> = ({
  formHelpers,
  onValuesChange,
  titleTranslationKey,
}) => {
  const [initialValues] = useState(personalDetailsInitialValues);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<Partial<PersonalDetailsFormNewModel>>>();

  useEffect(() => {
    setValidationSchema(createPersonalDetailsFormValidator());
  }, []);

  const {
    formSectionProps,
    onFormSubmitSuccess,
    onFormInitialValuesChange,
    onStartEditInSummaryMode,
    isDisabledFormSummary,
  } = formHelpers;

  const onSubmit = (values: PersonalDetailsFormNewModel) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(
        t(titleTranslationKey ?? `personalDetailsForm:title:mixed1`),
        isDisabledFormSummary
      )}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent
          onFormInitialValuesChange={onFormInitialValuesChange}
          disabled={isDisabledFormSummary}
          initialFormValue={initialValues}
          formTranslationKey="personalDetailsForm"
          onValuesChange={onValuesChange}
          formHelpers={formHelpers}
        />
      </Formik>
    </FormSection>
  );
};

export const FormContent = (props) => {
  const { values, submitForm } = useFormikContext<PersonalDetailsFormNewModel>();
  const { formTranslationKey, disabled, onValuesChange, formHelpers } = props;
  const { onFormValuesChange } = formHelpers;

  useEffect(() => {
    onValuesChange?.(values);
    onFormValuesChange(values);
  }, [values, onValuesChange, onFormValuesChange]);

  const snCSubmitted = !useSelector(uiStateSelectors.snCSubmittedMock);
  const disableAllFormControls = disabled || snCSubmitted;

  const [inputs, updateInputs] = useState<ReturnType<typeof createFormInputs>>(() =>
    createFormInputs(formTranslationKey, disableAllFormControls)
  );

  useEffect(() => {
    updateInputs(createFormInputs(formTranslationKey, disableAllFormControls));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formTranslationKey, disableAllFormControls]);

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
      <>
          <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3" s="1/1">
              {inputs.title()}
            </Layout.Item>
            <Layout.Item default="1/3" s="1/1">
              {inputs.firstName()}
            </Layout.Item>
            <Layout.Item default="1/3" s="1/1">
              {inputs.lastName()}
            </Layout.Item>
          </Layout>
        </Fieldset.Row>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3" s="1/1">
              {inputs.dateOfBirth()}
            </Layout.Item>
          </Layout>
        </Fieldset.Row>
        <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">
              {inputs.searchAddress()}
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.addressLine1()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.addressLine2()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.addressLine3()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
        <Fieldset.Row>
            <Layout>
              <Layout.Item default="1/2" s="1/1">{inputs.town()}</Layout.Item>
              <Layout.Item default="1/2" s="1/1">{inputs.county()}</Layout.Item>
            </Layout>
        </Fieldset.Row>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.eirCode()}</Layout.Item>
           </Layout>
        </Fieldset.Row>
        <FormSectionSubmitButton onClick={submitForm} formHelpers={formHelpers} />
      </>
    );
};
